import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../../lib/auth";
import { sucursalesApi } from "../../lib/api";
import { Trash2, Upload, Plus, ExternalLink, Clock, Timer, CheckCircle2 } from "lucide-react";

interface PdfEntry { nombre: string; url: string; }

interface Config {
  // Apariencia
  colorPrimario: string;
  colorSecundario: string;
  logoUrl: string | null;
  imagenFondoUrl: string | null;
  descripcionTienda: string;
  horarioAtencion: string;
  telefonoContacto: string;
  // Tipografía
  fuenteTitulo: string;
  fuenteTituloTamano: number;
  fuenteTituloColor: string;
  fuenteCuerpo: string;
  fuenteCuerpoTamano: number;
  fuenteCuerpoColor: string;
  // PDFs
  pdfs: PdfEntry[];
  // Redes sociales (de sucursal)
  whatsappNumero: string;
  instagramUrl: string;
  facebookUrl: string;
  tripadvisorUrl: string;
  websiteUrl: string;
  // Política
  politicaConfirmacionHoras: number;
  usaDuracionReserva: boolean;
  duracionDefaultMinutos: number;
  duracionOpciones: number[];
}

const FUENTES_TITULO = [
  "Playfair Display", "Lora", "Cormorant Garamond", "Merriweather",
  "Georgia", "Times New Roman",
  "Poppins", "Montserrat", "Raleway", "Josefin Sans", "Inter", "Roboto",
];
const FUENTES_CUERPO = [
  "Inter", "Roboto", "Open Sans", "Lato", "Source Sans Pro",
  "Poppins", "Montserrat",
  "Georgia", "Lora",
];

const TABS = ["Apariencia", "Tipografía", "Redes Sociales", "Política de Reservas"] as const;
type Tab = typeof TABS[number];

const DEFAULT: Config = {
  colorPrimario: "#C0392B", colorSecundario: "#2980B9",
  logoUrl: null, imagenFondoUrl: null,
  descripcionTienda: "", horarioAtencion: "", telefonoContacto: "",
  fuenteTitulo: "Playfair Display", fuenteTituloTamano: 28, fuenteTituloColor: "#1a1a1a",
  fuenteCuerpo: "Inter", fuenteCuerpoTamano: 15, fuenteCuerpoColor: "#4a4a4a",
  pdfs: [],
  whatsappNumero: "", instagramUrl: "", facebookUrl: "", tripadvisorUrl: "", websiteUrl: "",
  politicaConfirmacionHoras: 24, usaDuracionReserva: false,
  duracionDefaultMinutos: 60, duracionOpciones: [60, 90, 120],
};

export function MenuConfigPage() {
  const { token } = useAuth();
  const [sucursalId, setSucursalId] = useState(1);
  const [tab, setTab] = useState<Tab>("Apariencia");
  const [sucursales, setSucursales] = useState<{ id: number; nombre: string }[]>([]);
  const [sucursalesLoaded, setSucursalesLoaded] = useState(false);
  const [config, setConfig] = useState<Config>({ ...DEFAULT });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [loadError, setLoadError] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const [addingPdf, setAddingPdf] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevaOpcion, setNuevaOpcion] = useState("");

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // Cargar sucursales una sola vez y auto-seleccionar la primera
  useEffect(() => {
    sucursalesApi.list()
      .then(d => {
        const lista = d.map((s: any) => ({ id: s.id, nombre: s.nombre }));
        setSucursales(lista);
        if (lista.length > 0) setSucursalId(lista[0].id);
      })
      .catch(() => {})
      .finally(() => setSucursalesLoaded(true));
  }, []);

  // Cargar config cuando cambia la sucursal
  const cargar = useCallback(async (sid: number) => {
    if (!token) return;
    setLoaded(false);
    setLoadError("");
    try {
      const [menuRes, sucRes] = await Promise.all([
        fetch(`/api/v1/menu-publico/sucursal/${sid}/config`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/v1/sucursales/${sid}/config`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      let menuData: any = {};
      let sucData: any = {};
      try { menuData = menuRes.ok ? await menuRes.json() : {}; } catch { /* invalid json */ }
      try { sucData = sucRes.ok ? await sucRes.json() : {}; } catch { /* invalid json */ }
      if (!menuRes.ok && !sucRes.ok) {
        setLoadError(`Error cargando configuración (HTTP ${menuRes.status})`);
      }
      setConfig({
        colorPrimario:      menuData.colorPrimario     ?? "#C0392B",
        colorSecundario:    menuData.colorSecundario   ?? "#2980B9",
        logoUrl:            menuData.logoUrl           ?? null,
        imagenFondoUrl:     menuData.imagenFondoUrl    ?? null,
        descripcionTienda:  menuData.descripcionTienda ?? "",
        horarioAtencion:    menuData.horarioAtencion   ?? "",
        telefonoContacto:   menuData.telefonoContacto  ?? "",
        fuenteTitulo:       menuData.fuenteTitulo      ?? "Playfair Display",
        fuenteTituloTamano: menuData.fuenteTituloTamano ?? 28,
        fuenteTituloColor:  menuData.fuenteTituloColor  ?? "#1a1a1a",
        fuenteCuerpo:       menuData.fuenteCuerpo      ?? "Inter",
        fuenteCuerpoTamano: menuData.fuenteCuerpoTamano ?? 15,
        fuenteCuerpoColor:  menuData.fuenteCuerpoColor  ?? "#4a4a4a",
        pdfs:               Array.isArray(menuData.pdfs) ? menuData.pdfs : [],
        whatsappNumero:     sucData.whatsappNumero  ?? "",
        instagramUrl:       sucData.instagramUrl    ?? "",
        facebookUrl:        sucData.facebookUrl     ?? "",
        tripadvisorUrl:     sucData.tripadvisorUrl  ?? "",
        websiteUrl:         sucData.websiteUrl      ?? "",
        politicaConfirmacionHoras: sucData.politicaConfirmacionHoras ?? 24,
        usaDuracionReserva:        sucData.usaDuracionReserva ?? false,
        duracionDefaultMinutos:    sucData.duracionDefaultMinutos ?? 60,
        duracionOpciones:          sucData.duracionOpciones ?? [60, 90, 120],
      });
    } catch (e) {
      setLoadError(`Error de conexión: ${e instanceof Error ? e.message : String(e)}`);
    }
    setLoaded(true);
  }, [token]);

  useEffect(() => { cargar(sucursalId); }, [sucursalId, cargar]);

  const set = <K extends keyof Config>(k: K, v: Config[K]) =>
    setConfig(c => ({ ...c, [k]: v }));

  const uploadFile = async (file: File, tipo: "logo" | "bg" | "pdf", nombre?: string) => {
    setUploading(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sucursalId", String(sucursalId));
      fd.append("menuId", "0");
      const endpoint = tipo === "pdf" ? "/api/v1/upload/pdf" : "/api/v1/upload/logo";
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.url) {
        if (tipo === "logo") set("logoUrl", data.url);
        else if (tipo === "bg") set("imagenFondoUrl", data.url);
        else if (tipo === "pdf" && nombre) {
          setConfig(c => ({ ...c, pdfs: [...c.pdfs, { nombre, url: data.url }] }));
          setNuevoNombre(""); setAddingPdf(false);
        }
      } else {
        alert(`Error al subir: ${data.message ?? data.error ?? JSON.stringify(data)}`);
      }
    } catch (e: unknown) {
      alert(`Error de conexión: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setSavedMsg("");
    try {
      const results = await Promise.all([
        fetch(`/api/v1/menus/config/${sucursalId}`, {
          method: "PUT", headers,
          body: JSON.stringify({
            colorPrimario:      config.colorPrimario,
            colorSecundario:    config.colorSecundario,
            logoUrl:            config.logoUrl,
            imagenFondoUrl:     config.imagenFondoUrl,
            descripcionTienda:  config.descripcionTienda,
            horarioAtencion:    config.horarioAtencion,
            telefonoContacto:   config.telefonoContacto,
            fuenteTitulo:       config.fuenteTitulo,
            fuenteTituloTamano: config.fuenteTituloTamano,
            fuenteTituloColor:  config.fuenteTituloColor,
            fuenteCuerpo:       config.fuenteCuerpo,
            fuenteCuerpoTamano: config.fuenteCuerpoTamano,
            fuenteCuerpoColor:  config.fuenteCuerpoColor,
          }),
        }),
        fetch(`/api/v1/menus/config/${sucursalId}/pdfs`, {
          method: "PUT", headers,
          body: JSON.stringify(config.pdfs),
        }),
        fetch(`/api/v1/sucursales/${sucursalId}/config`, {
          method: "PUT", headers,
          body: JSON.stringify({
            whatsappNumero:            config.whatsappNumero,
            instagramUrl:              config.instagramUrl,
            facebookUrl:               config.facebookUrl,
            tripadvisorUrl:            config.tripadvisorUrl,
            websiteUrl:                config.websiteUrl,
            politicaConfirmacionHoras: config.politicaConfirmacionHoras,
            usaDuracionReserva:        config.usaDuracionReserva,
            duracionDefaultMinutos:    config.duracionDefaultMinutos,
            duracionOpciones:          config.duracionOpciones,
          }),
        }),
      ]);
      const failed = results.filter(r => !r.ok);
      if (failed.length > 0) {
        const detail = await failed[0].json().catch(() => ({}));
        setSavedMsg(`Error al guardar: ${detail.message ?? `HTTP ${failed[0].status}`}`);
      } else {
        setSavedMsg("Guardado correctamente");
        setTimeout(() => setSavedMsg(""), 3000);
        await cargar(sucursalId);
      }
    } catch {
      setSavedMsg("Error de conexión al guardar");
    } finally {
      setSaving(false);
    }
  };

  // Preview de tipografía
  const previewStyle = {
    fontFamily: config.fuenteTitulo,
    fontSize: `${config.fuenteTituloTamano}px`,
    color: config.fuenteTituloColor,
  };
  const previewBodyStyle = {
    fontFamily: config.fuenteCuerpo,
    fontSize: `${config.fuenteCuerpoTamano}px`,
    color: config.fuenteCuerpoColor,
  };

  if (!sucursalesLoaded || !loaded) return (
    <div className="flex items-center justify-center py-20 text-ink-400">Cargando...</div>
  );

  if (sucursales.length === 0) return (
    <div className="mx-auto max-w-md px-6 py-20 text-center space-y-3">
      <h2 className="text-lg font-semibold text-ink-700">Sin sucursales</h2>
      <p className="text-ink-500 text-sm">No se encontraron sucursales configuradas. Ejecuta la sincronización o contacta al administrador.</p>
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl px-6 py-10 space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink-800">Configuración del Menú</h1>
        <p className="text-ink-500 text-sm mt-1">Personaliza la apariencia, tipografía y políticas de cada sucursal.</p>
      </div>

      {/* Selector de sucursal */}
      <div className="flex gap-2 flex-wrap">
        {sucursales.map(s => (
          <button key={s.id} onClick={() => setSucursalId(s.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sucursalId === s.id ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
            {s.nombre}
          </button>
        ))}
      </div>

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {loadError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b border-ink-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-brand-600 text-brand-600" : "border-transparent text-ink-500 hover:text-ink-700"}`}>
            {t}
          </button>
        ))}
      </div>

      {/* ── APARIENCIA ── */}
      {tab === "Apariencia" && (
        <div className="space-y-5">
          {/* Logo */}
          <div className="card p-5 space-y-3">
            <h3 className="font-semibold text-ink-800">Logo del restaurante</h3>
            <div className="text-xs text-ink-500 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
              📐 <strong>Recomendado: 400 × 400 px</strong> · PNG con fondo transparente ideal · JPG aceptado · Máx 2 MB
            </div>
            <div className="flex items-center gap-4">
              {config.logoUrl
                ? <img src={config.logoUrl} alt="Logo" className="h-16 w-16 object-contain rounded-full border-2 border-ink-200 bg-ink-50" />
                : <div className="h-16 w-16 rounded-full bg-ink-100 border-2 border-dashed border-ink-300 flex items-center justify-center">
                    <span className="text-xl">🏪</span>
                  </div>
              }
              <div className="space-y-1.5">
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f, "logo"); }} />
                <button onClick={() => logoInputRef.current?.click()} disabled={!!uploading}
                  className="btn-secondary text-sm flex items-center gap-1.5">
                  <Upload className="size-3.5" />
                  {uploading === "logo" ? "Subiendo..." : "Subir logo"}
                </button>
                {config.logoUrl && (
                  <button onClick={() => set("logoUrl", null)}
                    className="text-xs text-red-500 hover:underline">Quitar</button>
                )}
              </div>
            </div>
          </div>

          {/* Imagen de fondo */}
          <div className="card p-5 space-y-3">
            <h3 className="font-semibold text-ink-800">Imagen de fondo</h3>
            <div className="text-xs text-ink-500 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
              📐 <strong>Recomendado: 1920 × 1080 px</strong> · JPG o PNG · Máx 5 MB · Se muestra con opacidad reducida detrás del contenido
            </div>
            <div className="flex items-center gap-4">
              {config.imagenFondoUrl
                ? <img src={config.imagenFondoUrl} alt="Fondo" className="h-16 w-24 object-cover rounded-lg border border-ink-200" />
                : <div className="h-16 w-24 bg-ink-100 border-2 border-dashed border-ink-300 rounded-lg flex items-center justify-center text-xl">🖼️</div>
              }
              <div className="space-y-1.5">
                <input ref={bgInputRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f, "bg"); }} />
                <button onClick={() => bgInputRef.current?.click()} disabled={!!uploading}
                  className="btn-secondary text-sm flex items-center gap-1.5">
                  <Upload className="size-3.5" />
                  {uploading === "bg" ? "Subiendo..." : "Subir imagen de fondo"}
                </button>
                {config.imagenFondoUrl && (
                  <button onClick={() => set("imagenFondoUrl", null)}
                    className="text-xs text-red-500 hover:underline">Quitar</button>
                )}
              </div>
            </div>
          </div>

          {/* Colores */}
          <div className="card p-5 space-y-3">
            <h3 className="font-semibold text-ink-800">Colores</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Color principal", key: "colorPrimario" as const, desc: "Header, botones, acentos" },
                { label: "Color secundario", key: "colorSecundario" as const, desc: "Botón de cuenta, secundarios" },
              ].map(({ label, key, desc }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-ink-700 mb-0.5">{label}</label>
                  <p className="text-xs text-ink-400 mb-2">{desc}</p>
                  <div className="flex items-center gap-3">
                    <input type="color" value={config[key]}
                      onChange={e => set(key, e.target.value)}
                      className="h-10 w-14 rounded-lg cursor-pointer border-2 border-ink-200" />
                    <input type="text" value={config[key]}
                      onChange={e => { if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) set(key, e.target.value); }}
                      className="input w-28 text-sm font-mono" placeholder="#000000" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Información */}
          <div className="card p-5 space-y-3">
            <h3 className="font-semibold text-ink-800">Información pública</h3>
            <div>
              <label className="block text-xs text-ink-600 mb-1">Descripción / eslogan</label>
              <textarea value={config.descripcionTienda}
                onChange={e => set("descripcionTienda", e.target.value)}
                rows={2} className="input w-full resize-none text-sm"
                placeholder="Bienvenidos a nuestro restaurante..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-ink-600 mb-1">Horario</label>
                <input value={config.horarioAtencion}
                  onChange={e => set("horarioAtencion", e.target.value)}
                  className="input w-full text-sm" placeholder="Lun-Dom 12:00-23:00" />
              </div>
              <div>
                <label className="block text-xs text-ink-600 mb-1">Teléfono público</label>
                <input value={config.telefonoContacto}
                  onChange={e => set("telefonoContacto", e.target.value)}
                  className="input w-full text-sm" placeholder="+58 212-555-0001" />
              </div>
            </div>
          </div>

          {/* PDFs */}
          <div className="card p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-ink-800">Cartas del menú (PDF)</h3>
                <p className="text-xs text-ink-500 mt-0.5">Comidas, Postres, Bebidas, etc.</p>
              </div>
              <a href={`/menu/sucursal/${sucursalId}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-brand-600 hover:underline">
                Ver <ExternalLink className="size-3" />
              </a>
            </div>
            <div className="space-y-2">
              {config.pdfs.map((pdf, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-ink-50 rounded-lg">
                  <span>📄</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-ink-800">{pdf.nombre}</p>
                    <p className="text-xs text-ink-400 truncate">{pdf.url}</p>
                  </div>
                  <button onClick={() => setConfig(c => ({ ...c, pdfs: c.pdfs.filter((_, i) => i !== idx) }))}
                    className="p-1 text-red-400 hover:text-red-600 rounded">
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
            {addingPdf ? (
              <div className="border-2 border-dashed border-brand-300 rounded-lg p-4 space-y-3">
                <div className="text-xs text-ink-500 bg-blue-50 rounded px-3 py-2 border border-blue-100">
                  📄 <strong>PDF del menú:</strong> Máx 20 MB · El cliente lo navega en su teléfono con scroll y zoom
                </div>
                <input type="text" placeholder="Nombre (ej: Comidas, Postres, Bebidas...)"
                  value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)}
                  className="input w-full text-sm" />
                <input ref={pdfInputRef} type="file" accept="application/pdf" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f && nuevoNombre.trim()) uploadFile(f, "pdf", nuevoNombre.trim()); else if (f) alert("Escribe el nombre primero."); }} />
                <div className="flex gap-2">
                  <button onClick={() => pdfInputRef.current?.click()}
                    disabled={!!uploading || !nuevoNombre.trim()}
                    className="btn-primary flex items-center gap-2 flex-1 justify-center text-sm">
                    <Upload className="size-4" />
                    {uploading === "pdf" ? "Subiendo..." : "Seleccionar PDF"}
                  </button>
                  <button onClick={() => { setAddingPdf(false); setNuevoNombre(""); }} className="btn-secondary text-sm">Cancelar</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingPdf(true)}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-ink-300 rounded-lg text-sm text-ink-500 hover:border-brand-400 hover:text-brand-600 transition-colors">
                <Plus className="size-4" /> Agregar carta PDF
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── TIPOGRAFÍA ── */}
      {tab === "Tipografía" && (
        <div className="space-y-5">
          {/* Preview */}
          <div className="card p-5 space-y-3" style={{ backgroundColor: config.colorPrimario + "10" }}>
            <h3 className="font-semibold text-ink-700 text-sm">Vista previa del menú</h3>
            <div className="bg-white rounded-xl p-5 border border-ink-100">
              <div style={previewStyle} className="mb-3 leading-tight">
                Nombre del restaurante
              </div>
              <div style={previewBodyStyle}>
                Descripción del plato · precio y detalles del menú
              </div>
            </div>
          </div>

          {/* Tipografía del título */}
          <div className="card p-5 space-y-4">
            <h3 className="font-semibold text-ink-800">Tipografía del título / nombre</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs text-ink-600 mb-1">Fuente del título</label>
                <select value={config.fuenteTitulo} onChange={e => set("fuenteTitulo", e.target.value)}
                  className="input w-full" style={{ fontFamily: config.fuenteTitulo }}>
                  {FUENTES_TITULO.map(f => (
                    <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-ink-600 mb-1">Tamaño (px)</label>
                  <div className="flex items-center gap-2">
                    <input type="range" min={18} max={60} value={config.fuenteTituloTamano}
                      onChange={e => set("fuenteTituloTamano", Number(e.target.value))}
                      className="flex-1" />
                    <span className="text-sm font-bold text-ink-700 w-10 text-right">{config.fuenteTituloTamano}px</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-ink-600 mb-1">Color del título</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={config.fuenteTituloColor}
                      onChange={e => set("fuenteTituloColor", e.target.value)}
                      className="h-9 w-12 rounded cursor-pointer border border-ink-200" />
                    <input type="text" value={config.fuenteTituloColor}
                      onChange={e => { if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) set("fuenteTituloColor", e.target.value); }}
                      className="input w-24 text-sm font-mono" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tipografía del cuerpo */}
          <div className="card p-5 space-y-4">
            <h3 className="font-semibold text-ink-800">Tipografía del cuerpo / texto</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs text-ink-600 mb-1">Fuente del cuerpo</label>
                <select value={config.fuenteCuerpo} onChange={e => set("fuenteCuerpo", e.target.value)}
                  className="input w-full" style={{ fontFamily: config.fuenteCuerpo }}>
                  {FUENTES_CUERPO.map(f => (
                    <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-ink-600 mb-1">Tamaño (px)</label>
                  <div className="flex items-center gap-2">
                    <input type="range" min={12} max={24} value={config.fuenteCuerpoTamano}
                      onChange={e => set("fuenteCuerpoTamano", Number(e.target.value))}
                      className="flex-1" />
                    <span className="text-sm font-bold text-ink-700 w-10 text-right">{config.fuenteCuerpoTamano}px</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-ink-600 mb-1">Color del texto</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={config.fuenteCuerpoColor}
                      onChange={e => set("fuenteCuerpoColor", e.target.value)}
                      className="h-9 w-12 rounded cursor-pointer border border-ink-200" />
                    <input type="text" value={config.fuenteCuerpoColor}
                      onChange={e => { if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) set("fuenteCuerpoColor", e.target.value); }}
                      className="input w-24 text-sm font-mono" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── REDES SOCIALES ── */}
      {tab === "Redes Sociales" && (
        <div className="card p-5 space-y-4">
          <p className="text-sm text-ink-500">Aparecen en el menú público y en la página de reservas.</p>
          {[
            { label: "💬 WhatsApp (solo número sin +)", key: "whatsappNumero" as const, placeholder: "584125550001", hint: "Ej: 584125550001" },
            { label: "📸 Instagram URL", key: "instagramUrl" as const, placeholder: "https://instagram.com/turesturante" },
            { label: "📘 Facebook URL", key: "facebookUrl" as const, placeholder: "https://facebook.com/turesturante" },
            { label: "🌟 TripAdvisor URL", key: "tripadvisorUrl" as const, placeholder: "https://tripadvisor.com/..." },
            { label: "🌐 Sitio web", key: "websiteUrl" as const, placeholder: "https://turesturante.com" },
          ].map(({ label, key, placeholder, hint }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-ink-700 mb-1">{label}</label>
              {hint && <p className="text-xs text-ink-400 mb-1">{hint}</p>}
              <input value={config[key] ?? ""} onChange={e => set(key, e.target.value)}
                className="input w-full text-sm" placeholder={placeholder} />
            </div>
          ))}
        </div>
      )}

      {/* ── POLÍTICA DE RESERVAS ── */}
      {tab === "Política de Reservas" && (
        <div className="space-y-4">
          <div className="card p-5 space-y-4">
            <h3 className="font-semibold text-ink-800 flex items-center gap-2">
              <Clock className="size-4 text-ink-500" /> Tiempo de confirmación
            </h3>
            <p className="text-sm text-ink-500">
              Si la reserva no se confirma X horas antes del turno, se cancela automáticamente.
              <strong> Solo aplica a reservas web/públicas.</strong>
            </p>
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={168}
                value={config.politicaConfirmacionHoras}
                onChange={e => set("politicaConfirmacionHoras", Number(e.target.value))}
                className="input w-24 text-center text-lg font-bold" />
              <span className="text-ink-600 text-sm">horas antes del turno</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[6, 12, 24, 48].map(h => (
                <button key={h} onClick={() => set("politicaConfirmacionHoras", h)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${config.politicaConfirmacionHoras === h ? "bg-brand-600 text-white border-brand-600" : "border-ink-300 text-ink-600 hover:border-ink-500"}`}>
                  {h}h
                </button>
              ))}
            </div>
          </div>

          <div className="card p-5 space-y-4">
            <h3 className="font-semibold text-ink-800 flex items-center gap-2">
              <Timer className="size-4 text-ink-500" /> Duración de reserva
            </h3>
            <p className="text-sm text-ink-500">Activa para canchas de pádel, pistas de bowling, salas de juego, etc.</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <div onClick={() => set("usaDuracionReserva", !config.usaDuracionReserva)}
                className={`w-11 h-6 rounded-full transition-colors relative ${config.usaDuracionReserva ? "bg-brand-600" : "bg-ink-300"}`}>
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${config.usaDuracionReserva ? "translate-x-5" : "translate-x-0"}`} />
              </div>
              <span className="text-sm font-medium text-ink-700">
                {config.usaDuracionReserva ? "Activado (reserva por duración)" : "Desactivado (reserva de mesa normal)"}
              </span>
            </label>
            {config.usaDuracionReserva && (
              <div className="pl-4 border-l-2 border-brand-200 space-y-3">
                <div className="flex items-center gap-2">
                  <input type="number" min={15} max={480} step={15}
                    value={config.duracionDefaultMinutos}
                    onChange={e => set("duracionDefaultMinutos", Number(e.target.value))}
                    className="input w-24 text-center" />
                  <span className="text-ink-600 text-sm">minutos por defecto</span>
                </div>
                <div>
                  <p className="text-xs text-ink-500 mb-2 font-medium">Opciones disponibles:</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {config.duracionOpciones.map((opt, idx) => (
                      <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-brand-50 text-brand-700 rounded-full text-sm font-medium">
                        {opt >= 60 ? `${Math.floor(opt/60)}h${opt%60 ? ` ${opt%60}min` : ""}` : `${opt}min`}
                        <button onClick={() => set("duracionOpciones", config.duracionOpciones.filter((_, i) => i !== idx))}
                          className="ml-1 text-brand-400 hover:text-brand-700">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input type="number" min={15} max={480} step={15}
                      value={nuevaOpcion} onChange={e => setNuevaOpcion(e.target.value)}
                      placeholder="Ej: 90" className="input w-24 text-center text-sm" />
                    <span className="self-center text-ink-500 text-sm">min</span>
                    <button onClick={() => {
                      const val = Number(nuevaOpcion);
                      if (val > 0 && !config.duracionOpciones.includes(val)) {
                        set("duracionOpciones", [...config.duracionOpciones, val].sort((a, b) => a - b));
                        setNuevaOpcion("");
                      }
                    }} className="btn-secondary text-sm"><Plus className="size-4" /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Botón guardar */}
      <div className="flex items-center gap-4">
        <button onClick={save} disabled={saving}
          className="btn-primary flex-1 py-3 text-base font-semibold">
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
        {savedMsg && (
          <div className={`flex items-center gap-2 text-sm font-medium ${savedMsg.includes("Error") ? "text-red-600" : "text-emerald-600"}`}>
            {!savedMsg.includes("Error") && <CheckCircle2 className="size-4" />}
            {savedMsg}
          </div>
        )}
      </div>

      {/* Preview QR */}
      <div className="card p-4 flex items-center justify-between bg-ink-50">
        <div>
          <p className="text-xs font-medium text-ink-600">URL pública del menú</p>
          <p className="text-xs font-mono text-ink-400">/menu/sucursal/{sucursalId}</p>
        </div>
        <a href={`/menu/sucursal/${sucursalId}`} target="_blank" rel="noreferrer"
          className="btn-secondary text-sm flex items-center gap-2">
          <ExternalLink className="size-4" /> Abrir
        </a>
      </div>
    </div>
  );
}
